/**
 * NOTE: For compatibility across platforms all calls **must** return a
 * `Promise` object!
 */

import * as Platform from '../Platform';
import { PlatformError, NotImplementedError } from '../Errors';
import { PlayerAbstract } from '../PlayerAbstract';
import { BrowserPlayer } from '../Browser/BrowserPlayer';
import { ReachPlayerHardwareIdGeneratorMixin } from '../mixins/ReachPlayerHardwareIdGeneratorMixin';
import { ReachAssetMixin } from './ReachAssetMixin';
import { Scheduler } from '../../Utility/Scheduler';
import { objectHasOwnProperty } from '../../Utility';
import { Signage, labOptions } from '../../Signage';
import { ReachAPI } from '../../ReachApi';
import { Log } from '../../AuxillaryService/Log';
import * as COM from '../COM';
import { $ } from '../../../vendor/jquery';
import _ from 'underscore';
import { moment } from '../../../vendor/moment';
import * as VideoService from '../../Utility/VideoService';
import { LogToREACHPlayer } from './LogToREACHPlayer';
import { REACHPlayerLogForwarderMechanism } from './REACHPlayerLogForwarderMechanism';
import { Permission } from '../Permissions';
import { Feature, hasUrlQueryFeature, isUrlQueryPlatform } from '../PlatformCheck';
import { isReachBasedScreenSaver } from '../ScreenSaver/utility';
import { escapeNamedValueKey } from '../SafeNamedValueFilename';
import { DexieProofOfPlayLogForwarder } from '../DexieProofOfPlayLogForwarder';

/* global REACH */

/**
 * @constructor
 * @augments PlayerAbstract
 * @implements HeartbeatInterface
 * @implements LogConfigurationInterface
 * @implements NamedValueCacheInterface
 * @implements NtpClockSkewInterface
 * @implements PlayerControlInterface
 * @implements PlayerPackageInterface
 * @implements ReloadInterface
 * @implements Rs232ComInterface
 * @implements ScreenshotInterface
 * @implements TimeZoneInterface
 * @implements UpdateInterface
 * @implements VideoAssetCacheInterface
 * @implements VolumeInterface
 * @implements TeamviewerInterface
 * @implements MultiWindowInterface
 * @mixes ReachPlayerHardwareIdGeneratorMixin
 * @todo `this._systemUpdate.platform` my not be accurate, find a better way to
 * get the system's platform
 */
function ReachPlayer() {
    PlayerAbstract.call(this);

    this._systemUpdateBlock = {};
    this._systemUpdateBlock.systemName = undefined;
    this._systemUpdateBlock.blacklist = undefined;

    // @todo move to `initializePlatform()`
    this.getHostPlatform().then(
        function (hostPlatform) {
            this._systemUpdateBlock.systemName = hostPlatform;
        }.bind(this)
    );

    this.addPermission(Permission.VLC_RTP);
    this.addPermission(Permission.VLC_RTMP);
    this.addPermission(Permission.VLC_HDMI);
    this.addPermission(Permission.RELAXED_NET_SECURITY);
}

ReachPlayer.prototype = Object.create(ReachAssetMixin(PlayerAbstract).prototype);
ReachPlayerHardwareIdGeneratorMixin.call(ReachPlayer.prototype);
ReachPlayer.prototype.constructor = ReachPlayer;

/**
 * @inheritDoc
 */
ReachPlayer.prototype.type = Platform.REACH_PLAYER;

/**
 * @inheritDoc
 */
ReachPlayer.prototype.name = 'REACH Player';

/**
 * The interval ID of the heartbeat timer
 * @type {undefined|number}
 * @protected
 */
ReachPlayer.prototype._heartbeatIntervalId = undefined;

/**
 * Name of the `Scheduler` running the NTP Time Updates
 * @type {string}
 * @protected
 * @readonly
 */
ReachPlayer.prototype._ntpUpdateSchedulerName = 'Platform:ntp_sync';

/**
 * The amount of time to skew the NTP Time by.
 * @type {number}
 * @protected
 * @deprecated See {@link NtpClockSkewInterface.setNtpClockSkew}
 * @todo Figure out time unit (i.e. seconds or milliseconds)
 */
ReachPlayer.prototype._ntpClockSkew = 0;

/**
 * @type {import('../interfaces/UpdateInterface.ts').SystemUpdateObject | undefined}
 * @protected
 */
ReachPlayer.prototype._systemUpdate = undefined;

/**
 * @type {{systemName: string, blacklist?: Array<string>}}
 * @protected
 */
ReachPlayer.prototype._systemUpdateBlock = undefined;

/**
 * @returns {boolean}
 */
ReachPlayer.isTheRunningPlatform = function () {
    const isScreenSaver = isReachBasedScreenSaver();

    const thisPlatformIsRunning = isUrlQueryPlatform('Reach');
    const thisPlatformSupportsPmc = hasUrlQueryFeature(Feature.PostMessageClient);
    if (typeof thisPlatformIsRunning === 'boolean') {
        return thisPlatformIsRunning && isScreenSaver !== true && !thisPlatformSupportsPmc;
    }

    // use fallback check
    const isReach = typeof REACH !== 'undefined' && typeof REACH.CEF !== 'undefined';
    return isReach && isScreenSaver !== true && !thisPlatformSupportsPmc;
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.initializePlatform = async function () {
    await PlayerAbstract.prototype.initializePlatform.call(this);

    return new Promise(
        function (resolve) {
            try {
                // TODO: replace with `await this.isAtLeastVersion( '3.4' )`
                var versionParts = REACH.Application.GetVersion().split('.');
                if (
                    versionParts.length >= 2 &&
                    parseInt(versionParts[0]) > 2 &&
                    parseInt(versionParts[1]) > 3
                ) {
                    // use https://asset.reach.local for player version >= 3.4
                    Log.Info(
                        'programmatically setting useFsVideoProtocol to `true`',
                        null,
                        __fileline
                    );
                    labOptions.instance.modify('useFsVideoProtocol', true);
                }
            } catch (useFsVideoProtocolError) {
                console.warn(
                    'Failed to determine if the FS video protocol should be used.',
                    useFsVideoProtocolError
                );
                /*
            We will blindly catch this because who knows what
            REACH.Application.GetVersion() might throw and this initialization
            is an especially non-critical action that patches Player Software
            behavior.
            */
            }

            try {
                var fsWhitelist = REACH.NamedValueCache.GetValueOrDefault('fs_whitelist', null);
                if (fsWhitelist) {
                    fsWhitelist = JSON.parse(fsWhitelist);

                    if (
                        !_.some(fsWhitelist, function (whitelist) {
                            return (
                                whitelist ===
                                '{SystemDrive}\\ProgramData\\REACH Media Network\\Prefetch\\Install'
                            );
                        })
                    ) {
                        fsWhitelist.push(
                            '{SystemDrive}\\ProgramData\\REACH Media Network\\Prefetch\\Install'
                        );

                        REACH.NamedValueCache.SetValue(
                            'fs_whitelist',
                            JSON.stringify(fsWhitelist),
                            false
                        );
                    }
                }
            } catch (fsWhitelistError) {
                /*
            2018-05-24 NLD: We will blindly catch this because who knows what
            REACH.NamedValueCache() might throw and this initialization is an
            especially non-critical action that patches Player Software
            behavior.

            2020-11-25 JJJ: NLD, you should know what REACH.NamedValueCache
            might throw, as I believe you wrote that code.  Either way it seems
            this try/catch is here because of paranoia rather than because of a
            known error.
            */
                console.warn("Failed to modify the 'fs_whitelist' Named Value.", fsWhitelistError);
            }

            this.StartCEFVitalityFileUpdater();

            try {
                if (REACH.Application?.DestroyAllChromeWindows !== undefined) {
                    REACH.Application.DestroyAllChromeWindows();
                }
            } catch (exc) {
                // just want to make an attempt to destroy all previously instantiated
                // chrome windows in case the browser was refreshed manually (eg via
                // ctrl f5)
            }

            resolve();
        }.bind(this)
    );
};

/**
 * @inheritDoc
 * @todo use native promises
 */
ReachPlayer.prototype.clearBrowserCacheOnReboot = function () {
    var jqDeferred = $.Deferred();

    REACH.CEF.ClearCache();
    jqDeferred.resolve();

    return jqDeferred.promise();
};

/**
 * @inheritDoc
 * @todo use native promises
 */
ReachPlayer.prototype.getPlayerVersion = function () {
    var version = '1.0.0.0';

    try {
        version = REACH.Application.GetVersion();
    } catch (e) {
        this._logWarning('Failed to retrieve platform version: ' + e.toString());
    }

    return Promise.resolve(version);
};

/**
 * @inheritDoc
 * @todo use native promises
 */
ReachPlayer.prototype.getScreenshot = function () {
    var jqDeferred = $.Deferred();

    try {
        var resultAsString = REACH.Application.CaptureScreen();
        var result = JSON.parse(resultAsString);

        if (result && result.data) {
            jqDeferred.resolve({
                base64Image: result.data,
            });
        } else {
            jqDeferred.reject({
                error: {
                    message: 'invalid result from application controller',
                },
                result: resultAsString,
            });
        }
    } catch (e) {
        jqDeferred.reject({
            error: {
                message: e.message,
                name: e.name,
            },
        });
    }

    return jqDeferred.promise();
};

/**
 * Retrieves an array of information about a player's files located on
 * reach.store
 * @inheritDoc
 */
ReachPlayer.prototype.getListOfRootStorageFiles = function () {
    return new Promise((resolve, reject) => {
        try {
            var resultAsString = REACH.Application.GetListOfRootStorageFiles();
            var result = JSON.parse(resultAsString);

            if (result && objectHasOwnProperty(result, 'data')) {
                //Convert c# object to JS
                result.data.forEach((fiInfo, i) => {
                    result.data[i] = {
                        fileName: fiInfo.FileName,
                        fileExtension: fiInfo.FileExtension,
                        fullPath: fiInfo.FullPath,
                    };
                });

                resolve({ diFiles: result.data });
            } else {
                //This should use a system generated error
                reject(new Error('Invalid result from application controller.'));
            }
        } catch (error) {
            reject(error);
        }
    });
};

/**
 * Resolves encoded file data for the requested reach.store file.  Rejects
 * Error, file missing or exception thrown
 * @inheritDoc
 */
ReachPlayer.prototype.getRootStorageFile = function (fiName) {
    return new Promise((resolve, reject) => {
        if (!fiName) {
            reject(new Error('A file name is required for this operation.'));
        }
        try {
            var resultAsString = REACH.Application.GetRootStorageFile(fiName);
            var result = JSON.parse(resultAsString);

            if (result && objectHasOwnProperty(result, 'data')) {
                resolve({ fiString: result.data });
            } else {
                //This should use a system generated error
                reject(new Error('Invalid result from application controller.'));
            }
        } catch (error) {
            reject(error);
        }
    });
};

/**
 * @inheritDoc
 * @todo use native promises
 */
ReachPlayer.prototype.getProbeCOMPorts = function () {
    var jqDeferred = $.Deferred();

    try {
        COM.Rs232Display.probeComPorts().then(
            function (result) {
                jqDeferred.resolve({ attachedDevices: result });
            },
            function (e) {
                jqDeferred.reject({
                    error: {
                        message: e.message,
                        name: e.name,
                    },
                });
            }
        );
    } catch (e) {
        jqDeferred.reject({
            error: {
                message: e.message,
                name: e.name,
            },
        });
    }

    return jqDeferred.promise();
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.rebootSystem = async function () {
    if (REACH.HostController && REACH.HostController.Reboot) {
        await this.setNamedValue('last_restart', String(moment().format('YYYY-MM-DD HH:mm:ss')));
        REACH.HostController.Reboot();
    }
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.reloadSignage = function () {
    // Dispose of old stuff before reloading
    if (REACH && REACH.ExacqVision) {
        REACH.ExacqVision.DestroyCameraElements();
    }
    if (REACH.VLC) {
        REACH.VLC.DestroyAllVlcControls();
    }

    if (typeof REACH.CEF !== 'undefined' && typeof REACH.CEF.ReloadStartingUrl !== 'undefined') {
        // Redirect to the Player Software's internal starting page
        REACH.CEF.ReloadStartingUrl();
    } else {
        BrowserPlayer.prototype.reloadSignage.call(this);
    }
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.setNtpClockSkew = async function (skewAmount) {
    this._ntpClockSkew = skewAmount;
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.enableNtp = async function (server, interval) {
    const supportsNtp = REACH.Host && REACH.Host.GetNetworkTimeDifference;

    if (!supportsNtp) {
        await this.setNamedValue('disable_os_ntp_service', 'false');

        throw new NotImplementedError('NTP updating not supported.');
    }

    await this.setNamedValue('disable_os_ntp_service', 'true');

    Scheduler.New()
        .Called(this._ntpUpdateSchedulerName)
        .HasAction(() => {
            this._performNtpUpdate(server, this._ntpClockSkew);
        })
        .RunEvery(interval, 'seconds');
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.disableNtp = async function () {
    await this.setNamedValue('disable_os_ntp_service', 'false');
};

/**
 * @inheritDoc
 * @param {import('../interfaces/UpdateInterface.ts').SystemUpdateObject} systemUpdate
 */
ReachPlayer.prototype.schedulePlatformUpdate = function (systemUpdate) {
    /*
        set NamedValueCache key "platform_update_enabled" based on value
        retrieved from API. This tells RDS Modular Service Host to enable to
        disable 'Windows Update' service allowing clients to manage Windows
        update themselves.
        */
    try {
        REACH.NamedValueCache?.SetValue(
            'platform_update_enabled',
            systemUpdate.isEnabled ? 'true' : 'false',
            true
        );
    } catch (e) {
        // The original developer failed to document what exception they were
        // trying to catch, so it is unknown why this try/catch block is here.
    }

    this._systemUpdate = systemUpdate;
    this._schedulePlatformUpdate();
    this._scheduleThirtyDaysAutomaticPlatformUpdate(systemUpdate.lastUpdated);
};

/**
 * **Feature:** Start a `Scheduler` to update the platform's system.
 * @param {string} [dateTime] - Date and time to run the update (format: 'YYYY-MM-DD HH:mm:ss')
 * @private
 */
ReachPlayer.prototype._schedulePlatformUpdate = function (dateTime) {
    var scheduledMoment = dateTime ? moment(dateTime) : null;
    var timeDifference = null;
    var currentMoment = moment();

    if (REACH.Rpc) {
        if (scheduledMoment === null || !scheduledMoment.isValid()) {
            //find scheduled date
            scheduledMoment = moment(this._systemUpdate.time, 'HH:mm:ss', true).day(
                this._systemUpdate.day
            );

            if (currentMoment > scheduledMoment) {
                scheduledMoment.add(7, 'days'); //next week
            }
        }
        //find time difference from now, in milliseconds
        timeDifference = moment.duration(scheduledMoment.diff(currentMoment)).asMilliseconds();

        //update the player after time difference and begin new timeout
        Log.Info(
            'Scheduling platform update on ' +
                scheduledMoment.format('dddd, MMMM Do YYYY, h:mm:ss a') +
                ' (' +
                timeDifference +
                ' ms)'
        );

        //Entry point for update
        this._getBlacklistedSystemUpdates().then(
            function (blacklistedUpdates) {
                Scheduler.New()
                    .Called('REACH_Platform_Update')
                    .HasAction(
                        function () {
                            this._runSystemUpdate(blacklistedUpdates);
                        }.bind(this)
                    )
                    .Starting(moment(Date.now() + timeDifference));
            }.bind(this),
            function () {
                //Retry in case of failure
                setTimeout(this._schedulePlatformUpdate.bind(this), 15000, dateTime);
            }.bind(this)
        );
    }
};

/**
 * **Feature:** Schedules the platform's system to update 30 days from last
 * update date (Runs update immediately if last update date is more than 30
 * days).
 * @param {string | null} defaultValue
 * @private
 */
ReachPlayer.prototype._scheduleThirtyDaysAutomaticPlatformUpdate = function (defaultValue) {
    //Check if platform update was ever run
    //var lastUpdateDateMoment = moment(this._systemUpdate.lastUpdated);
    // use local storage because there seems to be a disconnect with sending the
    // value to the API/log and getting it back updated (causes continuous
    // windows updating). Fallback to the old method if localstorage does not
    // return a value.
    var lastUpdate = localStorage ? localStorage.getItem('lastPlatformUpdate') : null;
    if (lastUpdate === null) {
        lastUpdate = defaultValue;
    }
    var lastUpdateDateMoment = moment(lastUpdate);

    if (lastUpdateDateMoment.isValid()) {
        //if platform update hasn't been run for 30 days run it in 15 seconds
        var futureMomentThirty = lastUpdateDateMoment.add(30, 'days');
        if (futureMomentThirty < moment()) {
            this._schedulePlatformUpdate(moment().add(15, 'seconds').format('YYYY-MM-DD HH:mm:ss'));
        } else {
            //schedule 30 day auto update
            var futureUpdate = futureMomentThirty.utc().format('YYYY-MM-DD HH:mm:ss');
            this._schedulePlatformUpdate(futureUpdate);
        }
    }
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.startSendingHeartbeat = function () {
    if (REACH && REACH.Application && REACH.Application.Ping) {
        this._heartbeatIntervalId = setInterval(function () {
            REACH.Application.Ping(window.location.href);
        }, 1000);
    }
};

/**
 * @inheritDoc
 * @todo convert to asynchronous function
 */
ReachPlayer.prototype.setVolume = function (volume, transitionTime) {
    try {
        var sessions = REACH.HostController.EnumerateAudioSession();
        var reachSessions = sessions.filter(function (session) {
            return session.indexOf('REACH') > -1;
        });
        if (reachSessions.length) {
            reachSessions.forEach(function (session) {
                REACH.HostController.SetAudioSessionVolume(session, volume, transitionTime);
            });
        } else {
            Log.Info('No REACH audio session');
        }
    } catch (err) {
        Log.Info(err.message, null, __fileline, err);
    }
};

/**
 * returns then-able jquery promise with
 * platform update blacklist as data
 * @return {Promise.<Array.<string>>}
 * @protected
 * @todo use native promises
 */
ReachPlayer.prototype._getBlacklistedSystemUpdates = function () {
    var dfd = $.Deferred();

    if (this._systemUpdateBlock.blacklist) {
        dfd.resolve(this._systemUpdateBlock.blacklist);
    } else {
        ReachAPI.player
            .getUpdateBlacklist(Signage.playerUuid, this._systemUpdateBlock.systemName)
            .then(
                function (data) {
                    this._systemUpdateBlock.blacklist =
                        data.blacklist[this._systemUpdateBlock.systemName];
                    dfd.resolve(this._systemUpdateBlock.blacklist);
                }.bind(this),
                dfd.reject
            );
    }

    return dfd.promise();
};

/**
 * Synchronize local system time with remote NTP server
 * @param {string} server
 * @param {number} clockSkew
 * @protected
 */
ReachPlayer.prototype._performNtpUpdate = function (server, clockSkew) {
    Log.Info(
        'Synchronizing local system with remote NTP server `' + server + '`',
        null,
        __fileline
    );

    REACH.Host.GetNetworkTimeDifference(
        server,
        function (timeDifference) {
            var timeDifferenceWithSkew = timeDifference + clockSkew;
            var clockWillBeAdjusted = Math.abs(timeDifferenceWithSkew) > 0.5;

            Log.Info(
                'NTP response received, timeDifference=`' +
                    timeDifference +
                    '`, clockSkew=`' +
                    clockSkew +
                    '`, timeDifferenceWithSkew=`' +
                    timeDifferenceWithSkew +
                    '`, clockWillBeAdjusted=`' +
                    clockWillBeAdjusted +
                    '`'
            );

            if (clockWillBeAdjusted) {
                REACH.Host.AdjustSystemClock(timeDifferenceWithSkew);
            }
        },
        function (errorMessage) {
            //failed to update from server
            Log.Error('Failed to synchronize the host with remote NTP server.', null, __fileline, {
                errorMessage: errorMessage,
                ntpServer: server,
            });
        }
    );
};

/**
 * Updates Platform
 * Reschedules platform update
 * @param {Array.<string>} blacklistedUpdates
 * @protected
 */
ReachPlayer.prototype._runSystemUpdate = function (blacklistedUpdates) {
    /**
     * Update ended notice saved to sqlite database
     */
    var endNotice = function () {
        Log.Notice('Platform update ended.', 'PLATFORM_UPDATE_ENDED', __fileline);
    }.bind(this);

    /**
     * Success Callback after platform update
     */
    var onPlatformUpdateSuccess = function (data) {
        Log.Notice('Platform update success.', 'PLATFORM_UPDATE_SUCCESS', __fileline, {
            message: data,
        });
        endNotice();

        //reschedule (for next week)
        this._schedulePlatformUpdate();
    }.bind(this);

    /**
     * Failure callback after platform update fail
     */
    var onPlatformUpdateFailure = function (data) {
        Log.Error('Player update failed.', null, __fileline);
        Log.Notice('Platform update failed.', 'PLATFORM_UPDATE_FAILURE', __fileline, {
            message: data,
        });
        endNotice();

        //reschedule (for next week)
        this._schedulePlatformUpdate();
    }.bind(this);

    try {
        localStorage.setItem('lastPlatformUpdate', moment().toString());
    } catch (error) {
        Log.Warning(
            "Failed to store 'lastPlatformUpdate'. Either `localStorage` is not supported on this platform, or it is full. Message: " +
                error,
            null,
            __fileline
        );
    }

    /**
     * Invokes C# function to run updates
     */
    REACH.Rpc.InvokeMethodAsyncWithCallbacks(
        'RunPlatformUpdateService',
        [blacklistedUpdates],
        onPlatformUpdateSuccess,
        onPlatformUpdateFailure
    );

    Log.Notice('Platform update initiated.', 'PLATFORM_UPDATE_INITIATED', __fileline);
};

//START: NamedValueCache <editor-fold desc="NamedValueCache">

/**
 * @inheritDoc
 * @todo use native promises
 */
ReachPlayer.prototype.deleteNamedValue = function (keyName) {
    var jqDeferred = $.Deferred();

    REACH.NamedValueCache.DeleteValue(escapeNamedValueKey(keyName));
    jqDeferred.resolve();

    return jqDeferred.promise();
};

/**
 * @inheritDoc
 * @todo use native promises
 */
ReachPlayer.prototype.getNamedValue = function (keyName, defaultValue) {
    var jqDeferred = $.Deferred();
    var keyValue = REACH.NamedValueCache.GetValue(escapeNamedValueKey(keyName));

    if (keyValue === null) {
        keyValue = defaultValue;
    }

    jqDeferred.resolve(keyValue);

    return jqDeferred.promise();
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.setNamedValue = async function (keyName, keyValue) {
    REACH.NamedValueCache.SetValue(escapeNamedValueKey(keyName), String(keyValue));
};
//END: NamedValueCache </editor-fold desc="NamedValueCache">

//START: Package Deployment <editor-fold desc="Package Deployment">
/**
 * @inheritDoc
 * @todo use native promises
 */
ReachPlayer.prototype.getInstalledPackages = function () {
    var jqDeferred = $.Deferred();

    if (REACH.Rpc) {
        try {
            REACH.Rpc.InvokeMethodAsyncWithCallbacks(
                'GetInstalledPackages',
                null,
                function (result) {
                    jqDeferred.resolve(JSON.parse(result));
                },
                function (errorMessage) {
                    jqDeferred.reject(new PlatformError('Unknown error: ' + errorMessage));
                }
            );
        } catch (e) {
            jqDeferred.reject(e);
        }
    } else {
        jqDeferred.reject(new PlatformError('`REACH.Rpc` is not implemented'));
    }

    return jqDeferred.promise();
};

/**
 * @inheritDoc
 * @todo use native promises
 */
ReachPlayer.prototype.deployPackage = function (
    notificationChannel,
    playerUuid,
    guid,
    version,
    purgeDownloadedPackage
) {
    var jqDeferred = $.Deferred();

    if (REACH.Rpc) {
        try {
            REACH.Rpc.InvokeMethodAsyncWithCallbacks(
                'RedeployPackage',
                [notificationChannel, playerUuid, guid, version, purgeDownloadedPackage],
                function () {
                    jqDeferred.resolve();
                },
                function (errorMessage) {
                    jqDeferred.reject(new PlatformError('Unknown error: ' + errorMessage));
                }
            );
        } catch (e) {
            jqDeferred.reject(e);
        }
    } else {
        jqDeferred.reject(new PlatformError('`REACH.Rpc` is not implemented'));
    }

    return jqDeferred.promise();
};
//END: Package Deployment </editor-fold desc="Package Deployment">

/**
 * @typedef VideoDownloadAttemptInformation
 * @type {Object}
 * @property {number} attempts
 * @property {number} firstAttempt
 * @property {number} lastAttempt
 * @property {number} timeSinceLastAttempt
 */

/**
 * @static
 * @type {Object.<string, VideoDownloadAttemptInformation>}
 */
ReachPlayer.videoAssetDownloadAttempts = {};
ReachPlayer.videoAssetDownloadAttemptCap = 5; //Five attempts maximum
ReachPlayer.videoAssetDownloadAttemptRateCap = 1000 * 60 * 3; //Once per three minutes

/**
 * Checks whether a video asset is allowed to attempt to download
 * @param {string} assetUuid
 * @returns {boolean}
 */
function videoDownloadIsAllowed(assetUuid) {
    if (!objectHasOwnProperty(ReachPlayer.videoAssetDownloadAttempts, assetUuid)) {
        return true;
    }

    var assetAttemptInfo = ReachPlayer.videoAssetDownloadAttempts[assetUuid];
    if (assetAttemptInfo.attempts >= ReachPlayer.videoAssetDownloadAttemptCap) {
        return false;
    }

    var timeSinceLastAttempt = Date.now() - assetAttemptInfo.lastAttempt;
    if (timeSinceLastAttempt < ReachPlayer.videoAssetDownloadAttemptRateCap) {
        return false;
    }

    return true;
}

/**
 * Records a download attempt for a particular video asset
 * @param {string} assetUuid
 */
function logVideoDownloadAttempt(assetUuid) {
    if (!objectHasOwnProperty(ReachPlayer.videoAssetDownloadAttempts, assetUuid)) {
        ReachPlayer.videoAssetDownloadAttempts[assetUuid] = {
            attempts: 1,
            firstAttempt: Date.now(),
            lastAttempt: Date.now(),
            timeSinceLastAttempt: 0,
        };
    } else {
        var assetAttemptInfo = ReachPlayer.videoAssetDownloadAttempts[assetUuid];
        assetAttemptInfo.attempts++;
        assetAttemptInfo.timeSinceLastAttempt = Date.now() - assetAttemptInfo.lastAttempt;
        assetAttemptInfo.lastAttempt = Date.now();
    }
}

/**
 * @param {string} assetUuid
 * @returns {boolean}
 */
ReachPlayer.prototype.prefetchVideo = function (assetUuid) {
    if (!videoDownloadIsAllowed(assetUuid)) {
        return false;
    }

    var isVideoCached = REACH.CEF.PrefetchVideo(assetUuid);
    if (!isVideoCached) {
        logVideoDownloadAttempt(assetUuid);
    }

    return isVideoCached;
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.cacheVideoAsset = function (assetUuid) {
    return Promise.resolve(this.prefetchVideo(assetUuid));
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.isCachedAsset = function (assetUuid) {
    return this.cacheVideoAsset(assetUuid);
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.getVideoPath = function (assetUuid) {
    var result = VideoService.getGenericPath(assetUuid);
    if (this.prefetchVideo(assetUuid)) {
        if (
            typeof REACH.CEF.GetVideoRealPath !== 'undefined' &&
            labOptions.instance.get('useFsVideoProtocol')
        ) {
            //available in player version 1.1.0.9
            result =
                'https://asset.reach.local/' +
                REACH.CEF.GetVideoRealPath(assetUuid).split('\\').join('/');
        } else {
            //considered for deprecation, embedded web server will hopefully be removed eventually
            result = 'http://localhost:23897/video?id=' + assetUuid;
        }
    }
    return Promise.resolve(result);
};

/**
 * @inheritDoc
 * @returns {Promise}
 */
ReachPlayer.prototype.getTopLiteDBLogs = function (channel, limit) {
    if (typeof REACH.Application.GetTopLiteDBLogs !== 'undefined') {
        REACH.Application.GetTopLiteDBLogs(channel, limit);
    }
    return Promise.resolve();
};

/**
 * @inheritDoc
 * @returns {Promise}
 */
ReachPlayer.prototype.deleteTopLiteDBLogs = function (channel, limit) {
    if (typeof REACH.Application.DeleteTopLiteDBLogs !== 'undefined') {
        REACH.Application.DeleteTopLiteDBLogs(channel, limit);
    }
    return Promise.resolve();
};

//START: CEFVitality </editor-fold desc="CEFVitality">
/**
 * This function will begin writing to a file every sixty seconds which will
 * indicate to the REACH Player Software that JavaScript is still running.
 * (requires player version 1.6.3 or greater)
 *
 * Does a similar job as `startSendingHeartbeat()` but is executed as soon
 * as the platform is initialized.
 */
ReachPlayer.prototype.StartCEFVitalityFileUpdater = function () {
    if (this.CEFVitalityFileUpdaterRunning) {
        return;
    }
    this.CEFVitalityFileUpdaterRunning = true;

    setInterval(
        function () {
            this.UpdateCEFVitalityFile();
        }.bind(this),
        60 * 1000
    ); // 60s is used because a long running JS process may be running and that would block the UpdateCEFVitalityFile fcn from running
};

/**
 * called by the CEFVitalityFileUpdater. Used to tell CEF that the JS is
 * still responsive
 */
ReachPlayer.prototype.UpdateCEFVitalityFile = function () {
    REACH.NamedValueCache.SetValue('cef_vitality', moment().format('YYYY-MM-DD HH:mm:ss'));
};
//END: CEFVitality </editor-fold desc="CEFVitality">

//START: TimeZoneInterface </editor-fold desc="TimeZoneInterface">
/**
 * @inheritDoc
 */
ReachPlayer.prototype.setTimeZone = function (timeZone) {
    return new Promise(function (resolve, reject) {
        // I don't know why we attempt to retry, but "it was like that when I
        // got here".
        var retryAttempts = 0;
        var retryInterval = 5;
        var setTimeZone = function () {
            try {
                REACH.Host.SetTimeZone(timeZone);
                resolve({ rebootRequired: false });
            } catch (e) {
                if (retryAttempts >= 3) {
                    reject(e);
                } else {
                    retryAttempts++;
                    Log.Error(
                        'Failed to set system time zone (retry in ' + retryInterval + ' seconds)',
                        null,
                        __fileline,
                        e
                    );
                    setTimeout(setTimeZone, retryInterval * 1000);
                }
            }
        }.bind(this);

        setTimeZone();
    });
};
//END: TimeZoneInterface </editor-fold desc="TimeZoneInterface">

/**
 * Retrieve the Device ID used by Teamviewer to connect to the player.
 * @throws {PlatformError} if Teamviewer wasn't installed by REACH
 * @throws {PlatformError} if NamedValueCache is not implemented
 * @returns {Promise<string>}
 */
ReachPlayer.prototype.getRemoteSupportDeviceId = function () {
    return Promise.resolve().then(() => {
        let remoteSupportDeviceId;

        if (REACH.NamedValueCache && REACH.NamedValueCache.GetValueOrDefault) {
            const isReachTeamViewer =
                REACH.NamedValueCache.KeyExists &&
                REACH.NamedValueCache.KeyExists('teamviewer_device_alias');

            if (REACH.NamedValueCache.ReloadKey) {
                //reload key from disk if player software supports it
                REACH.NamedValueCache.ReloadKey('remote_support_device_id');
            }

            const remoteSupportDeviceIdNamedValue = REACH.NamedValueCache.GetValueOrDefault(
                'remote_support_device_id',
                null
            );

            if (isReachTeamViewer && remoteSupportDeviceIdNamedValue) {
                remoteSupportDeviceId = remoteSupportDeviceIdNamedValue;
            } else {
                throw new PlatformError(
                    'NamedValueCache key remote_support_device_id is not defined or TeamViewer instance does not belong to REACH'
                );
            }
        } else {
            throw new PlatformError('Host does not implement NamedValueCache.GetValueOrDefault');
        }

        return remoteSupportDeviceId;
    });
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.configureLogging = async function (log) {
    if (typeof REACH !== 'undefined') {
        // Prefer REACH.Logger.LogMessage (new mechanism)
        if (REACH.Logger && REACH.Logger.LogMessage) {
            log.Mechanism.push(new REACHPlayerLogForwarderMechanism());
        } else if (REACH.Application && REACH.Application.Log) {
            log.Mechanism.push(new LogToREACHPlayer());
        } else {
            log.Mechanism.push(new DexieProofOfPlayLogForwarder());
        }
    }
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.createWindow = function ({ rectangle, url }) {
    if (
        typeof REACH !== 'undefined' &&
        typeof REACH?.Application?.CreateChromeWindow !== 'undefined'
    ) {
        return REACH.Application.CreateChromeWindow(
            url,
            Math.round(rectangle.left),
            Math.round(rectangle.top),
            Math.round(rectangle.width),
            Math.round(rectangle.height)
        );
    }
    return -1;
};

/**
 * @inheritDoc
 */
ReachPlayer.prototype.destroyWindow = function (handleId) {
    if (
        typeof REACH !== 'undefined' &&
        typeof REACH?.Application?.DestroyChromeWindow !== 'undefined'
    ) {
        return REACH.Application?.DestroyChromeWindow(handleId);
    }
    return false;
};

/**
 * @returns {JsonValue}
 */
ReachPlayer.prototype.executeJsOnWindow = function (handleId, jsToExecute) {
    if (
        typeof REACH !== 'undefined' &&
        typeof REACH?.Application?.ExecuteJSOnChromeWindow !== 'undefined'
    ) {
        const result = REACH.Application?.ExecuteJSOnChromeWindow(handleId, jsToExecute);

        if (result === '') {
            throw new Error(`Failed to find handle with the provided handleId: ${handleId}`);
        }

        if (result === null) {
            return null;
        }

        return JSON.parse(result);
    }

    return null;
};

export { ReachPlayer };
